/* Shared shell for the PUBLIC marketing pages only.
   - Exposes `marketingLinks` for each route's `links` export: the marketing
     stylesheet + Google Fonts (Archivo / Spline Sans Mono). Fonts load ONLY on
     these routes — the embedded admin (root.tsx) is untouched.
   - Header + Footer + the page <main> wrapper. */
import { Link } from "react-router";
import marketingHref from "../../styles/marketing.css?url";
import { BRAND, SOCIAL_IMAGE } from "../../brand";
import { ArrowRight } from "./icons";

const FONT_CSS =
  "https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,400..800;1,400..700&family=Spline+Sans+Mono:wght@400;500&display=swap";

/** Spread into a public route's `links` export. */
export const marketingLinks = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" as const },
  { rel: "stylesheet", href: FONT_CSS },
  { rel: "stylesheet", href: marketingHref },
];

/** Shared local social-card metadata for every public route. */
export const socialImageMeta = () => [
  { property: "og:image", content: SOCIAL_IMAGE.url },
  { property: "og:image:width", content: SOCIAL_IMAGE.width },
  { property: "og:image:height", content: SOCIAL_IMAGE.height },
  { property: "og:image:alt", content: SOCIAL_IMAGE.alt },
  { name: "twitter:card", content: "summary_large_image" },
  { name: "twitter:image", content: SOCIAL_IMAGE.url },
  { name: "twitter:image:alt", content: SOCIAL_IMAGE.alt },
];

export const APP_STORE_URL = "https://apps.shopify.com/labelforge-1";

const NAV = [
  { href: "/features", label: "Features" },
  { href: "/pricing", label: "Pricing" },
  { href: "/how-to-build-an-fda-nutrition-facts-label", label: "Guide" },
  { href: "/demo", label: "Demo" },
];

/** The brand mark: a tiny Nutrition Facts panel — heavy bar + two hairlines. */
export function BrandMark() {
  return (
    <span className="nm-brand__mark" aria-hidden>
      <span />
      <span />
      <span />
    </span>
  );
}

export function MarketingHeader() {
  return (
    <header className="nm-header">
      <div className="nm-wrap nm-header__inner">
        <Link to="/" className="nm-brand" aria-label={`${BRAND.name} home`}>
          <BrandMark />
          {BRAND.name}
        </Link>
        <nav className="nm-nav" aria-label="Primary">
          <div className="nm-nav__links">
            {NAV.map((n) => (
              <Link key={n.href} to={n.href}>
                {n.label}
              </Link>
            ))}
          </div>
          <a className="nm-btn nm-btn--dark" href={APP_STORE_URL}>
            Install free
            <ArrowRight />
          </a>
        </nav>
      </div>
    </header>
  );
}

const SIBLINGS = [
  { href: "https://apps.shopify.com/storelinter", label: "StoreLinter" },
  { href: "https://apps.shopify.com/product-quiz-2", label: "Product Quiz" },
  { href: "https://apps.shopify.com/quote-desk", label: "Quote Desk" },
  { href: "https://apps.shopify.com/geo-ify", label: "GEO — AI Shopping Optimization" },
];

export function MarketingFooter() {
  return (
    <footer className="nm-footer">
      <div className="nm-wrap">
        <div className="nm-footer__grid">
          <div className="nm-footer__brand">
            <Link to="/" className="nm-brand" aria-label={`${BRAND.name} home`}>
              <BrandMark />
              {BRAND.name}
            </Link>
            <p>
              FDA-style Nutrition Facts and Supplement Facts panels, built inside
              Shopify admin — with automatic 21 CFR rounding, USDA lookup, and
              300 DPI print-ready export. Free. By Legitimate LLC.
            </p>
          </div>

          <nav aria-label="Product">
            <h4>Product</h4>
            <ul>
              <li>
                <Link to="/features">Features</Link>
              </li>
              <li>
                <Link to="/pricing">Pricing</Link>
              </li>
              <li>
                <Link to="/demo">Live demo</Link>
              </li>
              <li>
                <a href={APP_STORE_URL}>Shopify App Store</a>
              </li>
            </ul>
          </nav>

          <nav aria-label="Resources">
            <h4>Resources</h4>
            <ul>
              <li>
                <Link to="/how-to-build-an-fda-nutrition-facts-label">
                  Build a Nutrition Facts label
                </Link>
              </li>
              <li>
                <Link to="/support">Support</Link>
              </li>
              <li>
                <Link to="/privacy">Privacy</Link>
              </li>
              <li>
                <Link to="/terms">Terms</Link>
              </li>
            </ul>
          </nav>

          <nav aria-label="More from Legitimate LLC">
            <h4>More apps</h4>
            <ul>
              {SIBLINGS.map((s) => (
                <li key={s.href}>
                  <a href={s.href}>{s.label}</a>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="nm-footer__bar">
          <span>
            © {new Date().getFullYear()} Legitimate LLC. Built for food, beverage
            &amp; supplement brands on Shopify.
          </span>
          <span>
            Questions:{" "}
            <a href={`mailto:${BRAND.supportEmail}`}>{BRAND.supportEmail}</a>
          </span>
        </div>
      </div>
    </footer>
  );
}

/** Full-page wrapper: skip link + .nm root + header + main + footer. */
export function MarketingShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="nm">
      <a className="nm-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />
      <main id="main">{children}</main>
      <MarketingFooter />
    </div>
  );
}
